import * as React from "react";
import styled from "styled-components";
import {
  AlarmClock,
  FolderClosed,
  FolderOpen,
  Key,
  PlusIcon,
  SquareCheckBig,
  Target,
} from "lucide-react";
import { withBlock, withBlocks } from "src/utils/withBlock";
import { SimpleTreeItemWrapper, TreeItemComponentProps } from "dnd-kit-sortable-tree";
import { AnyTaggedItem } from "src/types";
import s from "./Task.module.css";
import { Menu, Item, useContextMenu } from "react-contexify";
import { useIsoContext } from "src/isoContext";
import { Task as TaskComponent } from "src/components/Task";
import { TaskField } from "src/components/TaskField";
import { useLiveQuery } from "dexie-react-hooks";
import { getGoal } from "src/database/queries/goals";
import { taskFormContext } from "../TaskForm/taskFormContext";
import { deleteItem, updateItem } from "src/database/queries/items";
import { createEmptyHabit, createEmptyTask } from "src/database/factories";
import { Ordering } from "src/database/models/Ordering";

export const Task = React.forwardRef<HTMLDivElement, TreeItemComponentProps<AnyTaggedItem>>(
  function TaskInner(props, ref) {
    const taskForm = React.useContext(taskFormContext);
    const item = props.item;
    const MENU_ID = `task-${item.id}`;
    const { show } = useContextMenu({ id: MENU_ID });
    const { isLarge } = useIsoContext();
    const goalId = "goalId" in item ? item.goalId : null;
    const goal = useLiveQuery(() => (goalId ? getGoal(goalId) : null), [goalId]);
    const insertOrder = React.useMemo(() => {
      const children = item.children ?? [];
      return Ordering.insertAt(children, (t) => t.order, children.length);
    }, [item.children]);

    const setDisabled = (disabled: boolean) => {
      updateItem({ ...item, disabled });
    };

    const handleDelete = () => {
      deleteItem(item);
    };

    const handleCreateChild = () => {
      const inheritGoalId =
        (item.type === "habit" || item.type === "task") && item.goalId ? item.goalId : null;
      if (item.type === "habit") {
        taskForm.create(
          createEmptyHabit({
            parentId: item.id,
            order: insertOrder,
            goalId: inheritGoalId,
          }),
        );
      } else {
        taskForm.create(
          createEmptyTask({
            parentId: item.id,
            order: insertOrder,
            goalId: inheritGoalId,
          }),
        );
      }
    };

    return (
      <>
        <SimpleTreeItemWrapper
          {...props}
          ref={ref}
          showDragHandle={false}
          manualDrag={!isLarge}
          hideCollapseButton
          contentClassName={s.taskContent}
        >
          <TaskComponent
            item={item}
            onClick={() => taskForm.edit(item)}
            onContextMenu={(event) => show({ event })}
            handleProps={props.handleProps}
          >
            {"condition" in item && item.condition && (
              <TaskField icon={Key}>{item.condition}</TaskField>
            )}
            {withBlock(item.content, "timer", (b) => (
              <TaskField icon={AlarmClock}>{renderDuration(b.props.duration)}</TaskField>
            ))}
            {withBlocks(item.content, "checkListItem", (items) => (
              <TaskField icon={SquareCheckBig}>{items.length}</TaskField>
            ))}
            {goal && <TaskField icon={Target} title={goal.name} />}
            {item.children && item.children.length > 0 && (
              <TaskField
                data-testid="task-collapse-toggle"
                icon={props.collapsed ? FolderClosed : FolderOpen}
                onClick={() => props.onCollapse?.()}
              >
                {item.children.length}
              </TaskField>
            )}
            <Add data-testid="task-add-subtask" onClick={handleCreateChild}>
              <PlusIcon size={14} />
            </Add>
          </TaskComponent>
        </SimpleTreeItemWrapper>
        <Menu id={MENU_ID}>
          {item.disabled ? (
            <Item onClick={() => setDisabled(false)}>Enable</Item>
          ) : (
            <Item onClick={() => setDisabled(true)}>Disable</Item>
          )}
          <Item onClick={handleDelete}>Delete</Item>
        </Menu>
      </>
    );
  },
);

function renderDuration(duration: string) {
  const [seconds, minutes, hours] = duration
    .split(":")
    .reverse()
    .map((s) => parseInt(s, 10));
  let str = "";
  if (hours > 0) {
    str += hours + "h";
  }
  if (minutes > 0) {
    str += minutes + "m";
  }
  if (seconds > 0) {
    str += seconds + "s";
  }

  return str;
}

const Add = styled.div`
  padding: 10px;
  margin-right: -10px;
`;
